// src/features/inventory/pages/ItemDetailPage/tabs/Hazardous/index.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Edit as EditIcon,
  RemoveRedEye as EyeIcon,
  DeleteOutline as DisposalIcon,
  Shield as PPEIcon,
  Inventory2 as StorageIcon,
  WaterDrop as SpillIcon,
  HealthAndSafety as ShieldIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMSDS } from '../../../../hooks/useMSDS';
import { useInventoryMSDSPermissions } from '../../../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../../../hooks/mui/useSnackBar';
import { useFolders } from '../../../../../files/hooks/useFolders';
import { useFiles } from '../../../../../files/hooks/useFiles';
import { useFileUrl, downloadFileLink } from '../../../../../files/hooks/useFileUrl';
import { FileFullscreenViewer } from '../../../../../files/components/FileFullscreenViewer';
import { filesAPI } from '../../../../../files/services/files';
import SectionHeader from '../../../../../../components/common/SectionHeader';
import StaffMember from '../../../../../../components/common/StaffMember';
import EditMSDSDialog from './EditMSDSDialog';
import { HazardLevelLabel } from '../../../../components/HazardDot';

const MSDS_ROOT_FOLDER = 'i18n-files:i18n_folder_names.inventory_msds';

// Status drives the banner at the top of the tab: it is the one thing a user must
// notice (an expired or missing data sheet is a compliance problem).
function statusOf(msds) {
  if (!msds.is_valid) return { severity: 'error', icon: <ErrorIcon />, key: 'invalid' };
  if (msds.is_expired) return { severity: 'error', icon: <ErrorIcon />, key: 'expired' };
  if (msds.is_expiring_soon) return { severity: 'warning', icon: <WarningIcon />, key: 'expiring_soon' };
  return { severity: 'success', icon: <CheckIcon />, key: 'up_to_date' };
}

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('da-DK') : '—');

function Fact({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24, typography: 'body2', fontWeight: 500 }}>
        {children}
      </Box>
    </Box>
  );
}

// Ordered by urgency: what to do in an accident first, routine handling after.
const SAFETY_FIELDS = [
  { field: 'eye_contact', icon: EyeIcon, color: 'info' },
  { field: 'spill_handling', icon: SpillIcon, color: 'primary' },
  { field: 'ppe', icon: PPEIcon, color: 'success' },
  { field: 'storage', icon: StorageIcon, color: 'warning' },
  { field: 'disposal', icon: DisposalIcon, color: 'error' },
];

function SafetyRow({ title, content, icon: Icon, color, emptyLabel }) {
  return (
    <Box sx={{ display: 'flex', gap: 3, py: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
          color: `${color}.main`,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" component="h3" fontWeight={600} color="text.primary" sx={{ lineHeight: 1.4 }}>
          {title}
        </Typography>
        {content ? (
          <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
            {content}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled" fontStyle="italic" sx={{ mt: 0.5 }}>
            {emptyLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// The data sheet itself, streamed through a signed link so the browser's PDF
// viewer pages through it.
function SdsViewer({ file }) {
  const { t } = useTranslation(['inventory', 'files']);
  const { url, loading, error } = useFileUrl(file?.file_id);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (file?.file_id) filesAPI.markFileRead(file.file_id).catch(() => {});
  }, [file?.file_id]);

  if (!file) {
    return (
      <Paper variant="outlined" sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <PdfIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="body2" color="text.secondary">{t('inventory:msds.tab.no_pdf')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        // lg: fills the tab (the PDF is the only thing that scrolls); stacked
        // below the instructions on small screens it gets a fixed height
        height: { xs: '80vh', lg: '100%' },
        minHeight: 480,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <PdfIcon sx={{ color: 'error.main', fontSize: 20 }} />
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }} title={file.filename}>
          {file.filename}
        </Typography>
        <Tooltip title={t('files:actions.download')}>
          <IconButton size="small" onClick={() => downloadFileLink(file.file_id, { filename: file.filename })}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('files:actions.fullscreen')}>
          <IconButton size="small" onClick={() => setFullscreen(true)}>
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('files:messages.open_new_tab')}>
          <span>
            <IconButton size="small" disabled={!url} onClick={() => window.open(url, '_blank', 'noopener')}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        {loading && <CircularProgress />}
        {error && <Alert severity="error">{t('files:messages.no_preview')}</Alert>}
        {url && !loading && (
          <embed src={url} type="application/pdf" title={file.filename} style={{ width: '100%', height: '100%', border: 'none' }} />
        )}
      </Box>
      {fullscreen && (
        <FileFullscreenViewer
          fileId={file.file_id}
          filename={file.filename}
          filetype="application/pdf"
          onClose={() => setFullscreen(false)}
        />
      )}
    </Paper>
  );
}

export default function HazardousTab({ itemId, item, onMsdsChanged }) {
  const { t } = useTranslation(['inventory']);
  const { canCreate, canUpdate } = useInventoryMSDSPermissions();
  const { showSuccess, showError } = useSnackbar();
  const { msds, loading, fetchMSDS, createMSDS, updateMSDS } = useMSDS();
  const { t: tFiles } = useTranslation(['files']);
  const { getRootContents, createFolder, getFolderContents } = useFolders();
  const { uploadFile } = useFiles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [browseFolder, setBrowseFolder] = useState(null);

  useEffect(() => {
    if (!itemId) return;
    fetchMSDS(itemId).finally(() => setInitialLoaded(true));
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The item's folder under the safety data sheet folder, as { folderId, breadcrumbs }.
   * With `create` (uploading) missing folders are made; without it (just opening
   * the dialog) nothing is, and the nearest existing folder is returned instead,
   * so cancelling the dialog leaves no empty folder behind.
   */
  const findItemFolder = useCallback(async ({ create = false } = {}) => {
    try {
      const rootResult = await getRootContents();
      if (!rootResult?.success) return null;
      const rootData = rootResult.data.data;
      const trail = [{ id: null, name: tFiles('files:root') }];

      let rootMSDSFolderId;
      const existingRoot = rootData.folders?.find(f => f.folder_name === MSDS_ROOT_FOLDER);
      if (existingRoot) {
        rootMSDSFolderId = existingRoot.folder_id;
      } else if (create) {
        const cr = await createFolder({
          folder_name: MSDS_ROOT_FOLDER,
          description: 'System folder for inventory MSDS files',
          parent_folder_id: null,
          is_system_folder: false,
          is_protected: false,
        });
        if (!cr?.success) return null;
        rootMSDSFolderId = cr.data.data.folder_id;
      } else {
        return { folderId: null, breadcrumbs: trail };
      }
      trail.push({ id: rootMSDSFolderId, name: tFiles('files:i18n_folder_names.inventory_msds') });

      const contentsResult = await getFolderContents(rootMSDSFolderId);
      if (!contentsResult?.success) return null;
      const subfolders = contentsResult.data.data?.subfolders || [];
      const folderName = item?.name || itemId;

      let itemFolderId;
      const existing = subfolders.find(f => f.folder_name === folderName);
      if (existing) {
        itemFolderId = existing.folder_id;
      } else if (create) {
        const cr = await createFolder({
          folder_name: folderName,
          description: `MSDS files for ${folderName}`,
          parent_folder_id: rootMSDSFolderId,
          is_system_folder: false,
          is_protected: false,
        });
        if (!cr?.success) return null;
        itemFolderId = cr.data.data.folder_id;
      } else {
        return { folderId: rootMSDSFolderId, breadcrumbs: trail };
      }

      return { folderId: itemFolderId, breadcrumbs: [...trail, { id: itemFolderId, name: folderName }] };
    } catch {
      return null;
    }
  }, [getRootContents, createFolder, getFolderContents, item, itemId, tFiles]);

  const handleOpenDialog = useCallback(async () => {
    setBrowseFolder(await findItemFolder());
    setDialogOpen(true);
  }, [findItemFolder]);

  // A new PDF goes to the item's folder (created now if needed)
  const uploadToItemFolder = useCallback(async (file, { onUploadProgress } = {}) => {
    const folder = await findItemFolder({ create: true });
    return uploadFile(file, { folder_id: folder?.folderId ?? null, is_system_file: false, onUploadProgress });
  }, [findItemFolder, uploadFile]);

  const handleSave = async (body) => {
    setSaving(true);
    const fn = msds ? updateMSDS : createMSDS;
    const result = await fn(itemId, body);
    setSaving(false);
    if (result?.success) {
      showSuccess(t(msds ? 'inventory:msds.tab.save_success' : 'inventory:msds.tab.create_success'));
      setDialogOpen(false);
      // The page's hazard banner reads the level from the item
      onMsdsChanged?.();
    } else {
      showError(result?.data?.detail || t('inventory:msds.tab.save_error'));
    }
  };

  if (!initialLoaded || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const emptyLabel = t('inventory:msds.tab.empty');

  if (!msds) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <ErrorIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary">{t('inventory:msds.tab.empty_state.title')}</Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t('inventory:msds.tab.empty_state.description')}
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            {t('inventory:msds.tab.empty_state.add_button')}
          </Button>
        )}
        <EditMSDSDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSave={handleSave}
          existing={null}
          loading={saving}
          startingFolderId={browseFolder?.folderId ?? null}
          initialBreadcrumbs={browseFolder?.breadcrumbs ?? null}
          uploadToItemFolder={uploadToItemFolder}
        />
      </Box>
    );
  }

  const status = statusOf(msds);

  return (
    <Box
      sx={{
        p: 3,
        display: 'grid',
        gap: 4,
        alignItems: 'start',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 5fr) 7fr' },
        // lg: exactly the tab's height, so the tab itself never scrolls and the
        // PDF's own scrollbar is the only one (no scroller inside a scroller)
        flex: { lg: 1 },
        minHeight: { lg: 0 },
        gridTemplateRows: { lg: 'minmax(0, 1fr)' },
        overflow: { lg: 'hidden' },
      }}
    >
      <Box sx={{ minWidth: 0, maxHeight: { lg: '100%' }, overflowY: { lg: 'auto' } }}>
        <Alert
          severity={status.severity}
          icon={status.icon}
          variant="outlined"
          action={canUpdate && (
            <Button color="inherit" size="small" startIcon={<EditIcon />} onClick={handleOpenDialog}>
              {t('inventory:msds.tab.update_button')}
            </Button>
          )}
          sx={{ mb: 4, alignItems: 'center' }}
        >
          <Typography variant="subtitle1" fontWeight={600} component="div">
            {t(`inventory:msds.tab.status_chip.${status.key}`, { days: msds.days_until_review })}
          </Typography>
          <Typography variant="body2" component="div">
            {t('inventory:msds.tab.review_label')} {formatDate(msds.review_date)}
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 3, mb: 5 }}>
          <Fact label={t('inventory:msds.hazard_level.label')}>
            {msds.hazard_level ? (
              <HazardLevelLabel level={msds.hazard_level} />
            ) : (
              <Typography component="span" variant="body2" color="text.disabled" fontStyle="italic">
                {t('inventory:msds.hazard_level.none')}
              </Typography>
            )}
          </Fact>
          <Fact label={t('inventory:msds.columns.issued_date')}>{formatDate(msds.issued_date)}</Fact>
          <Fact label={t('inventory:msds.columns.language')}>{msds.language.toUpperCase()}</Fact>
          <Fact label={t('inventory:msds.columns.version')}>{`v${msds.version}`}</Fact>
          <Fact label={t('inventory:msds.tab.created_by')}>
            <StaffMember
              staffMember={msds.creator}
              avatarSize={22}
              imageSize="32x32"
              nameVariant="body2"
              gap={0.75}
              nameSx={{ fontWeight: 500 }}
              clickable
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              · {formatDate(msds.created_at)}
            </Typography>
          </Fact>
        </Box>

        <SectionHeader icon={ShieldIcon} title={t('inventory:msds.tab.safety_title')} />
        <Box sx={{ '& > * + *': { borderTop: 1, borderColor: 'divider' } }}>
          {SAFETY_FIELDS.map(({ field, icon, color }) => (
            <SafetyRow
              key={field}
              title={t(`inventory:msds.fields.${field}`)}
              content={msds[field]}
              icon={icon}
              color={color}
              emptyLabel={emptyLabel}
            />
          ))}
        </Box>
      </Box>

      {/* Keyed by revision: a new PDF version keeps the file id, so remount to fetch a fresh link */}
      <SdsViewer key={msds.msds_id} file={msds.file} />

      <EditMSDSDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        existing={msds}
        loading={saving}
        startingFolderId={browseFolder?.folderId ?? null}
        initialBreadcrumbs={browseFolder?.breadcrumbs ?? null}
        uploadToItemFolder={uploadToItemFolder}
      />
    </Box>
  );
}
